/**
 * ==========================================================
 * DELIVERY SCHEDULER
 * Shopify + Flatpickr
 * Versión 1.0
 * ==========================================================
 */

class DeliveryScheduler {

    constructor() {

        /**
         * Configuración
         */
        this.config = {

            // Hora límite para permitir entrega el mismo día.
            // 11 = 11:00 AM
            cutoffHour: 11,

            // Horario único
            deliveryWindow: "2:00 p.m. - 8:00 p.m.",

            // 0 Domingo
            // 6 Sábado
            disabledWeekDays: [0, 6],

            // Feriados configurables desde theme.liquid
            holidays: window.SHOP_DELIVERY_HOLIDAYS || [
                "2026-01-01", // Año Nuevo
                "2026-04-02", // Viernes Santo
                "2026-05-01", // Día del Trabajo
                "2026-06-29", // San Pedro y San Pablo
                "2026-07-28", // Fiestas Patrias
                "2026-07-29",
                "2026-08-30", // Santa Rosa de Lima
                "2026-10-08", // Combate de Angamos
                "2026-11-01", // Todos los Santos
                "2026-12-08", // Inmaculada Concepción
                "2026-12-25"  // Navidad
            ]

        };

        /**
         * Elementos HTML
         */

        this.elements = {

            dateInput: document.querySelector("#delivery-date"),

            timeInput: document.querySelector("#delivery-time"),

            saveButton: document.querySelector("#save-delivery"),

            message: document.querySelector("#delivery-message")

        };

        /**
         * Instancia Flatpickr
         */

        this.calendar = null;

        /**
         * Estado
         */

        this.loading = false;

    }

    /**
     * =====================================================
     * INIT
     * =====================================================
     */

    async init() {

        if (!this.elements.dateInput) {

            console.warn("DeliveryScheduler: #delivery-date no encontrado.");

            return;

        }

        if (typeof flatpickr === "undefined") {

            console.error("Flatpickr no está cargado.");

            return;

        }

        this.initCalendar();

        this.bindEvents();

        await this.restoreAttributes();

    }

    /**
     * =====================================================
     * HELPERS
     * =====================================================
     */

    formatDate(date) {

        const year = date.getFullYear();

        const month = String(date.getMonth() + 1).padStart(2, "0");

        const day = String(date.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;

    }

    isWeekend(date) {

        return this.config.disabledWeekDays.includes(date.getDay());

    }

    isHoliday(date) {

        return this.config.holidays.includes(
            this.formatDate(date)
        );

    }

    /**
     * Devuelve una copia sin horas
     */

    normalizeDate(date) {

        return new Date(

            date.getFullYear(),

            date.getMonth(),

            date.getDate()

        );

    }

    /**
     * Obtiene el siguiente día hábil
     */

    getNextBusinessDay(date) {

        const next = new Date(date);

        while (

            this.isWeekend(next) ||

            this.isHoliday(next)

        ) {

            next.setDate(next.getDate() + 1);

        }

        return next;

    }

    /**
     * Primera fecha permitida
     */

    getMinimumDate() {

        const now = new Date();

        const min = this.normalizeDate(now);

        /**
         * Si ya pasaron las 11am
         * empieza desde mañana
         */

        if (

            now.getHours() >= this.config.cutoffHour

        ) {

            min.setDate(

                min.getDate() + 1

            );

        }

        return this.getNextBusinessDay(min);

    }

    /**
     * =====================================================
     * CALENDARIO
     * =====================================================
     */

    initCalendar() {

        const minDate = this.getMinimumDate();

        this.calendar = flatpickr(

            this.elements.dateInput,

            {

                locale: flatpickr.l10ns.es,

                dateFormat: "Y-m-d",

                //altInput: true,

                //altFormat: "d/m/Y",

                allowInput: false,

                disableMobile: true,

                minDate: minDate,

                disable: [

                    (date) => {

                        return (

                            this.isWeekend(date) ||

                            this.isHoliday(date)

                        );

                    }

                ],

                onChange: (dates) => {

                    this.handleDateChange(dates);

                }

            }

        );

    }
        /**
     * =====================================================
     * EVENTOS
     * =====================================================
     */

    bindEvents() {

        if (this.elements.saveButton) {

            this.elements.saveButton.addEventListener(

                "click",

                () => {

                    this.save();

                }

            );

        }

    }

    /**
     * =====================================================
     * CAMBIO DE FECHA
     * =====================================================
     */

    handleDateChange(selectedDates) {

        if (!selectedDates.length) {

            return;

        }

        if (this.elements.timeInput) {

            this.elements.timeInput.value =
                this.config.deliveryWindow;

        }

        this.updateDeliveryMessage(selectedDates[0]);

    }

    /**
     * =====================================================
     * Actualiza el mensaje de entrega
     * =====================================================
     */

    updateDeliveryMessage(selectedDate) {

        if (!this.elements.message) {

            return;

        }

        const fecha = selectedDate.toLocaleDateString(

            "es-PE",

            {

                weekday: "long",

                day: "numeric",

                month: "long",

                year: "numeric"

            }

        );

        const horario = this.elements.timeInput

            ? this.elements.timeInput.value

            : "";

        this.showMessage(

            `Tu pedido será entregado el ${fecha} entre las ${horario}.`,

            "success"

        );

    }

    /**
     * =====================================================
     * VALIDACIONES
     * =====================================================
     */

    validate() {

        if (!this.elements.dateInput.value) {

            this.showMessage(

                "Selecciona una fecha de entrega.",

                "error"

            );

            return false;

        }

        return true;

    }

    /**
     * =====================================================
     * MENSAJES
     * =====================================================
     */

    showMessage(message, type = "success") {

        if (!this.elements.message) {

            return;

        }

        this.elements.message.textContent = message;

        this.elements.message.className =
            `delivery-message ${type}`;

    }

    clearMessage() {

        if (!this.elements.message) {

            return;

        }

        this.elements.message.textContent = "";

        this.elements.message.className =
            "delivery-message";

    }

    /**
     * =====================================================
     * LOADING
     * =====================================================
     */

    setLoading(state) {

        this.loading = state;

        if (!this.elements.saveButton) {

            return;

        }

        this.elements.saveButton.disabled = state;

        this.elements.saveButton.classList.toggle(

            "is-loading",

            state

        );

    }

    /**
     * =====================================================
     * SHOPIFY
     * Recupera atributos previamente guardados
     * =====================================================
     */

    async restoreAttributes() {

        try {

            const response = await fetch("/cart.js");

            if (!response.ok) {

                throw new Error("No se pudo obtener el carrito.");

            }

            const cart = await response.json();

            const attributes = cart.attributes || {};

            if (

                attributes.delivery_date &&

                this.calendar

            ) {

                this.calendar.setDate(

                    attributes.delivery_date,

                    true

                );

            }

            if (

                attributes.delivery_date &&

                this.elements.dateInput

            ) {

                this.elements.dateInput.value =
                    attributes.delivery_date;

            }

            if (

                attributes.delivery_time &&

                this.elements.timeInput

            ) {

                this.elements.timeInput.value =
                    attributes.delivery_time;

            }

        }

        catch (error) {

            console.error(

                "DeliveryScheduler:",

                error

            );

        }

    }

    /**
     * =====================================================
     * Guarda atributos en Shopify
     * =====================================================
     */

    async save() {

        if (this.loading) {

            return;

        }

        if (!this.validate()) {

            return;

        }

        this.setLoading(true);

        this.clearMessage();

        try {

            const response = await fetch(

                "/cart/update.js",

                {

                    method: "POST",

                    headers: {

                        "Content-Type":
                            "application/json"

                    },

                    body: JSON.stringify({

                        attributes: {

                            delivery_date:
                                this.elements.dateInput.value,

                            delivery_time:
                                this.config.deliveryWindow

                        }

                    })

                }

            );

            if (!response.ok) {

                throw new Error(

                    "No fue posible guardar la entrega."

                );

            }

            const cart = await response.json();
                        if (

                cart.attributes

            ) {

                this.showMessage(

                    "Fecha de entrega guardada correctamente."

                );

            }

        }

        catch (error) {

            console.error(

                "DeliveryScheduler:",

                error

            );

            this.showMessage(

                "No fue posible guardar la fecha de entrega.",

                "error"

            );

        }

        finally {

            this.setLoading(false);

        }

    }

    /**
     * =====================================================
     * MÉTODOS PÚBLICOS
     * =====================================================
     */

    getSelectedDate() {

        return this.elements.dateInput
            ? this.elements.dateInput.value
            : "";

    }

    getSelectedTime() {

        return this.config.deliveryWindow;

    }

    clearSelection() {

        if (this.calendar) {

            this.calendar.clear();

        }

        if (this.elements.dateInput) {

            this.elements.dateInput.value = "";

        }

        if (this.elements.timeInput) {

            this.elements.timeInput.value = "";

        }

        this.clearMessage();

    }

    reloadCalendar() {

        if (!this.calendar) {

            return;

        }

        this.calendar.set(

            "minDate",

            this.getMinimumDate()

        );

        this.calendar.redraw();

    }

}

/**
 * ==========================================================
 * INICIALIZACIÓN
 * ==========================================================
 */

document.addEventListener(

    "DOMContentLoaded",

    async () => {

        const scheduler = new DeliveryScheduler();

        await scheduler.init();

        window.deliveryScheduler = scheduler;

    }

);